import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CourierReviewApi } from '@shared/sdk-v1';
import type { CourierReview } from '@shared/sdk-v1';
import { apiConfig } from '../shared/api-config';
import { getUser } from '../state/authSlice';
import { getIsReviewsEnabled } from '../state/courierSlice';
import { showErrorToast, showSuccessToast } from '../utils/toast';

export interface SubmitReviewInput {
  rating: number;
  body: string;
  title?: string;
  packageId?: number;
}

export interface UseCustomerReviewsReturn {
  reviews: CourierReview[];
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  fetchMine: () => Promise<void>;
  /** Resolves true when the review was accepted (it lands as pending). */
  submitReview: (input: SubmitReviewInput) => Promise<boolean>;
}

const FRIENDLY_MESSAGES: Record<string, string> = {
  REVIEWS_DISABLED: 'Reviews are not available right now.',
  ALREADY_REVIEWED: 'You have already reviewed this package.',
  REVIEW_PENDING: 'You already have a review waiting for approval. Please wait for it to be reviewed before adding another.',
  PACKAGE_NOT_FOUND: 'We could not find that package on your account.',
  INVALID_RATING: 'Please choose a star rating from 1 to 5.',
  BODY_REQUIRED: 'Please write a few words about your experience.',
};

/** Pull the API error code/message out of an SDK ResponseError (or a plain Error). */
export async function readApiError(err: unknown): Promise<{ code?: string; message: string }> {
  const response = (err as { response?: Response })?.response;
  if (response && (typeof response.clone === 'function' || typeof response.json === 'function')) {
    try {
      const source = typeof response.clone === 'function' ? response.clone() : response;
      const body = await source.json();
      const code = body?.error?.code;
      return { code, message: FRIENDLY_MESSAGES[code] || body?.error?.message || 'Something went wrong' };
    } catch {
      // fall through
    }
  }
  return { message: err instanceof Error ? err.message : 'Something went wrong' };
}

export const useCustomerReviews = (): UseCustomerReviewsReturn => {
  const user = useSelector(getUser);
  const isReviewsEnabled = useSelector(getIsReviewsEnabled);
  const [reviews, setReviews] = useState<CourierReview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMine = useCallback(async () => {
    if (!user?.id || !isReviewsEnabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const api = new CourierReviewApi(apiConfig());
      setReviews(await api.courierReviewMine());
    } catch (err) {
      const info = await readApiError(err);
      setError(info.message);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, isReviewsEnabled]);

  useEffect(() => {
    fetchMine();
  }, [fetchMine]);

  const submitReview = useCallback(
    async (input: SubmitReviewInput): Promise<boolean> => {
      setIsSubmitting(true);
      try {
        const api = new CourierReviewApi(apiConfig());
        await api.courierReviewSubmit({ data: input });
        showSuccessToast({ title: 'Thank you!', message: "Your review will appear once it's approved." });
        await fetchMine();
        return true;
      } catch (err) {
        const info = await readApiError(err);
        showErrorToast({ title: 'Could not submit review', message: info.message });
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetchMine]
  );

  return { reviews, isLoading, isSubmitting, error, fetchMine, submitReview };
};
